import type { StateCreator } from 'zustand';
import type { SliceMutators } from '~/store/mutators';
import type { RootState } from '~/store/slices';
import type { Rect } from '~/types/pipeline';
import type { BoxRef } from '~/types/selection';
import { MIN_GRID_GAP } from './constants';
import { selectColDividers, selectRowDividers } from './selectors';

export type GridSlice = {
  grid: {
    cropRect: Rect | null;
    rowDividers: number[] | null;
    colDividers: number[] | null;
    individualRows: boolean;
    individualCols: boolean;
    autoConfidence: number | null;
  };
  setCropRect: (r: Rect) => void;
  setRowDividers: (xs: number[]) => void;
  setColDividers: (xs: number[]) => void;
  setIndividualRows: (b: boolean) => void;
  setIndividualCols: (b: boolean) => void;
  setAutoConfidence: (n: number) => void;
  setBoxWidth: (box: BoxRef, widthPx: number) => void;
  setBoxHeight: (box: BoxRef, heightPx: number) => void;
  /** Set width of multiple boxes in a single undoable step. */
  setBoxesWidth: (boxes: BoxRef[], widthPx: number) => void;
  /** Set height of multiple boxes in a single undoable step. */
  setBoxesHeight: (boxes: BoxRef[], heightPx: number) => void;
  /** Drop manual divider edits when crop changes. */
  clearDividers: () => void;
  resetGrid: () => void;
};

/**
 * Resize cell at `i` (between dividers[i] and dividers[i+1]) to `target` size,
 * anchored on its center. Crop edges (dividers[0] and dividers[last]) are never
 * moved, so a boundary cell falls back to anchoring on the immovable edge.
 * Each side independently clamps to MIN_GRID_GAP from its neighbor.
 */
function resizeCellAroundCenter(dividers: number[], i: number, target: number): void {
  const last = dividers.length - 1;
  const left = dividers[i];
  const right = dividers[i + 1];
  if (left === undefined || right === undefined) return;
  const center = (left + right) / 2;
  const want = Math.max(MIN_GRID_GAP, target);
  const halfDesired = want / 2;

  const isLeftFixed = i === 0;
  const isRightFixed = i + 1 === last;

  let newLeft = left;
  let newRight = right;

  if (isLeftFixed && isRightFixed) {
    return;
  } else if (isLeftFixed) {
    const next = dividers[i + 2];
    if (next === undefined) return;
    newRight = Math.min(next - MIN_GRID_GAP, left + want);
  } else if (isRightFixed) {
    const prev = dividers[i - 1];
    if (prev === undefined) return;
    newLeft = Math.max(prev + MIN_GRID_GAP, right - want);
  } else {
    const prev = dividers[i - 1];
    const next = dividers[i + 2];
    if (prev === undefined || next === undefined) return;
    const lLo = prev + MIN_GRID_GAP;
    const rHi = next - MIN_GRID_GAP;
    newLeft = Math.max(lLo, center - halfDesired);
    newRight = Math.min(rHi, center + halfDesired);
    // Enforce min cell size if both clamps tightened beyond reach.
    if (newRight - newLeft < MIN_GRID_GAP) {
      const c = (newLeft + newRight) / 2;
      newLeft = c - MIN_GRID_GAP / 2;
      newRight = c + MIN_GRID_GAP / 2;
    }
  }

  dividers[i] = Math.round(newLeft);
  dividers[i + 1] = Math.round(newRight);
}

/**
 * Apply a centroid-anchored size change along one axis to a list of boxes,
 * mutating the matching divider list on `s.grid`. No-op when crop is missing,
 * the divider list is too short, or `boxes` is empty.
 */
function resizeBoxesAxis(s: RootState, boxes: BoxRef[], sizePx: number, axis: 'row' | 'col'): void {
  if (!s.grid.cropRect || boxes.length === 0) return;
  const dividers = axis === 'col' ? selectColDividers(s) : selectRowDividers(s);
  if (dividers.length < 2) return;
  const base = [...dividers];
  for (const box of boxes) {
    const i = axis === 'col' ? box.col : box.row;
    if (i < 0 || i >= base.length - 1) continue;
    if (base.length === 2 && i === 0) {
      const edge = base[1];
      if (edge === undefined) continue;
      base.splice(1, 0, edge);
    }
    resizeCellAroundCenter(base, i, sizePx);
  }
  if (axis === 'col') s.grid.colDividers = base;
  else s.grid.rowDividers = base;
}

const INITIAL_GRID = {
  cropRect: null,
  rowDividers: null,
  colDividers: null,
  individualRows: false,
  individualCols: false,
  autoConfidence: null,
} satisfies GridSlice['grid'];

export const createGridSlice: StateCreator<GridSlice, SliceMutators, [], GridSlice> = (set) => ({
  grid: INITIAL_GRID,
  setCropRect: (r) =>
    set((draft) => {
      const s = draft as unknown as RootState;
      const old = s.grid.cropRect;
      if (old && old.x === r.x && old.y === r.y && old.w === r.w && old.h === r.h) return;
      s.grid.cropRect = r;
      // Regions are in pre-crop source coords; drop them on crop change.
      if (s.background.regions.length > 0) s.background.regions = [];
      if (!old || old.w <= 0 || old.h <= 0) return;
      const remap = (
        vals: number[] | null,
        oldStart: number,
        oldLen: number,
        newStart: number,
        newLen: number,
      ) =>
        vals ? vals.map((v) => Math.round(newStart + ((v - oldStart) / oldLen) * newLen)) : null;
      s.grid.colDividers = remap(s.grid.colDividers, old.x, old.w, r.x, r.w);
      s.grid.rowDividers = remap(s.grid.rowDividers, old.y, old.h, r.y, r.h);
    }),
  setRowDividers: (xs) =>
    set((s) => {
      s.grid.rowDividers = xs;
    }),
  setColDividers: (xs) =>
    set((s) => {
      s.grid.colDividers = xs;
    }),
  setIndividualRows: (b) =>
    set((s) => {
      s.grid.individualRows = b;
    }),
  setIndividualCols: (b) =>
    set((s) => {
      s.grid.individualCols = b;
    }),
  setAutoConfidence: (n) =>
    set((s) => {
      s.grid.autoConfidence = n;
    }),
  setBoxWidth: (box, widthPx) =>
    set((draft) => {
      resizeBoxesAxis(draft as unknown as RootState, [box], widthPx, 'col');
    }),
  setBoxHeight: (box, heightPx) =>
    set((draft) => {
      resizeBoxesAxis(draft as unknown as RootState, [box], heightPx, 'row');
    }),
  setBoxesWidth: (boxes, widthPx) =>
    set((draft) => {
      resizeBoxesAxis(draft as unknown as RootState, boxes, widthPx, 'col');
    }),
  setBoxesHeight: (boxes, heightPx) =>
    set((draft) => {
      resizeBoxesAxis(draft as unknown as RootState, boxes, heightPx, 'row');
    }),
  clearDividers: () =>
    set((s) => {
      s.grid.rowDividers = null;
      s.grid.colDividers = null;
    }),
  resetGrid: () =>
    set((s) => {
      s.grid = { ...INITIAL_GRID };
    }),
});
